import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import EmailCaptureModal from './EmailCaptureModal';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiDollarSign, FiClock, FiAward, FiCalendar, FiUsers, FiLock, FiHelpCircle, FiBarChart2 } = FiIcons;

const ResultsSection = ({ results, inputs }) => {
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showROIHelp, setShowROIHelp] = useState(false);
  const [showPaybackHelp, setShowPaybackHelp] = useState(false);
  const [showNetValueHelp, setShowNetValueHelp] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }).format(num);
  };

  // Only show 2 benefits in preview mode
  const previewBenefitItems = [
    { label: 'Increased Donations', value: results.donationIncrease, icon: FiTrendingUp, color: 'text-green-600 bg-green-50' },
    { label: 'Increased Grants', value: results.grantIncrease, icon: FiAward, color: 'text-indigo-600 bg-indigo-50' }
  ];

  // All benefits (shown after email capture)
  const allBenefitItems = [
    ...previewBenefitItems,
    { label: 'Annual Time Savings', value: results.annualTimeSavings, icon: FiClock, color: 'text-blue-600 bg-blue-50' },
    { label: 'Event Improvements', value: results.eventImprovement, icon: FiCalendar, color: 'text-purple-600 bg-purple-50' },
    { label: 'Volunteer Value', value: results.volunteerValue, icon: FiUsers, color: 'text-orange-600 bg-orange-50' }
  ];

  const handleEmailSubmit = async ({ email, firstName }) => {
    // Here you would send data to your backend or API
    // This is where you'd save to Google Sheets
    // Mock API call - replace with actual implementation
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log("Submitted:", {
      email,
      firstName,
      calculatorType: 'website',
      operatingBudget: inputs.annualOperatingBudget,
      marketingBudget: inputs.marketingBudget,
      websiteBudget: inputs.websiteBudget,
      threeYearValue: results.threeYearValue,
      paybackMonths: results.paybackMonths
    });

    // Close the modal after successful submission
    setTimeout(() => setShowEmailModal(false), 2000);
    return true;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="space-y-6"
    >
      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-wow-gradient rounded-2xl shadow-wow p-8 text-white"
      >
        <h3 className="text-2xl font-bold mb-6">Your Website ROI Opportunity</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center relative group">
            <button
              className="absolute right-0 top-0 p-1 text-white/70 hover:text-white"
              onClick={() => setShowROIHelp(!showROIHelp)}
              aria-label="Show ROI explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showROIHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>Return on Investment (ROI) shows the percentage return you'll get from your website investment. For example, 200% means you'll get back double your investment in annual benefits.</p>
                <button
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowROIHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatNumber(results.roi || 0)}%</div>
            <div className="text-white/80">Return on Investment</div>
          </div>
          <div className="text-center relative group">
            <button
              className="absolute right-0 top-0 p-1 text-white/70 hover:text-white"
              onClick={() => setShowPaybackHelp(!showPaybackHelp)}
              aria-label="Show payback explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showPaybackHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>Months to Payback shows how quickly your website investment will pay for itself through increased donations, grants, and efficiency. After this period, all benefits are pure gain.</p>
                <button
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPaybackHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatNumber(results.paybackMonths || 0)}</div>
            <div className="text-white/80">Months to Payback</div>
          </div>
          <div className="text-center relative group">
            <button
              className="absolute right-0 top-0 p-1 text-white/70 hover:text-white"
              onClick={() => setShowNetValueHelp(!showNetValueHelp)}
              aria-label="Show net value explanation"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
            {showNetValueHelp && (
              <div className="absolute z-10 bg-white text-gray-800 p-3 rounded-lg shadow-lg text-sm w-64 left-1/2 -translate-x-1/2 top-full mt-2">
                <p>3-Year Net Value is the total financial benefit over three years minus your initial investment. This shows the long-term impact of your website investment.</p>
                <button
                  className="absolute right-1 top-1 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNetValueHelp(false)}
                >
                  <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                </button>
              </div>
            )}
            <div className="text-3xl font-bold">{formatCurrency(results.threeYearValue || 0)}</div>
            <div className="text-white/80">3-Year Net Value</div>
          </div>
        </div>
      </motion.div>

      {/* Benefits Breakdown Explanation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-[#eaf3ff] p-5 rounded-xl"
      >
        <div className="flex items-start">
          <SafeIcon icon={FiBarChart2} className="text-wow-blue mt-1 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-gray-800 mb-1">How We Calculate Your ROI</h4>
            <p className="text-sm text-gray-700">
              Your website investment generates returns through increased donations and grants, time savings for staff, improved event attendance, and better volunteer recruitment. These benefits compound over time to create significant financial impact.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Benefits Breakdown - PREVIEW VERSION */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl shadow-wow p-8"
      >
        <h4 className="text-xl font-bold text-gray-900 mb-6">Financial Benefits You're Missing Annually</h4>
        {/* Preview benefits (just 2) */}
        <div className="space-y-4">
          {previewBenefitItems.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 * index }}
              className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${item.color}`}>
                  <SafeIcon icon={item.icon} className="text-lg" />
                </div>
                <span className="font-medium text-gray-700">{item.label}</span>
              </div>
              <span className="text-lg font-bold text-gray-900">
                {formatCurrency(item.value || 0)}
              </span>
            </motion.div>
          ))}

          {/* Locked benefits preview */}
          <div
            className="border border-gray-200 rounded-lg p-4 bg-gray-50 cursor-pointer"
            onClick={() => setShowEmailModal(true)}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gray-200">
                  <SafeIcon icon={FiLock} className="text-lg text-wow-blue" />
                </div>
                <span className="font-medium text-gray-500">3 More Benefit Categories</span>
              </div>
              <span className="text-gray-500 hover:text-wow-blue">Unlock Full Report</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="h-2 rounded-full bg-[#d6e8ff]" style={{ width: '40%' }}></div>
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-gray-200">
                  <SafeIcon icon={FiDollarSign} className="text-lg text-gray-600" />
                </div>
                <span className="font-bold text-gray-900">Preview Benefits</span>
              </div>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency((results.donationIncrease || 0) + (results.grantIncrease || 0))}
              </span>
            </div>

            {/* Locked total benefits */}
            <div
              className="mt-3 p-3 border border-gray-200 rounded-lg bg-gray-50 flex items-center justify-between cursor-pointer"
              onClick={() => setShowEmailModal(true)}
            >
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiLock} className="text-wow-blue" />
                <span className="text-gray-500 font-medium">Total Annual Benefits</span>
              </div>
              <span className="text-gray-500 hover:text-wow-blue">Unlock Report</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* CTA for email capture */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-2xl shadow-wow p-8 text-center"
      >
        <h4 className="text-xl font-bold text-gray-900 mb-4">
          Unlock Your Complete ROI Analysis
        </h4>
        <p className="text-gray-600 mb-6">
          Get your full report with all 5 benefit categories, custom recommendations, and actionable next steps.
        </p>
        <button
          onClick={() => setShowEmailModal(true)}
          className="bg-wow-gradient hover:shadow-wow-hover hover:-translate-y-1 transition-all text-white font-semibold px-8 py-3 rounded-lg inline-block"
        >
          Get My Complete Analysis
        </button>
      </motion.div>

      {/* Email capture modal */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSubmit={handleEmailSubmit}
        calculatorType="website"
        previewData={results}
      />
    </motion.div>
  );
};

export default ResultsSection;